/**
 * Data Storage Reconciliation and Cojoining Engine
 *
 * Merges and reconciles data stores from reclaimed local-first state,
 * legacy workspace OPFS documents, and browser localStorage into active CRDT documents.
 */

import * as Y from 'yjs';
import { todayLocalDate } from '@yada/types';

export interface HabitItem {
  id: string;
  name: string;
  color: string;
  icon?: string;
  rhythmType: string;
  rhythmConfig?: {
    daysOfWeek?: number[];
    intervalWeeks?: number;
    timesPerPeriod?: number;
  };
  reminderTime?: string;
  createdAt?: string;
  trackingStyle?: string;
  trackStreaks?: boolean;
  notes?: string;
}

export interface HabitEventItem {
  habitId: string;
  date: string;
}

/**
 * Cojoin two habit arrays by ID, merging properties when IDs collide
 * and taking the union of unique habits.
 */
export function cojoinHabits(current: any[] = [], reclaimed: any[] = []): HabitItem[] {
  const sanitize = (raw: any): HabitItem | null => {
    if (!raw || typeof raw !== 'object') return null;
    if (typeof raw.id !== 'string' || !raw.id.trim()) return null;
    return {
      id: raw.id.trim(),
      name: typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim() : 'Untitled Habit',
      color: typeof raw.color === 'string' && raw.color.trim() ? raw.color.trim() : '#4f8cff',
      icon: typeof raw.icon === 'string' ? raw.icon : undefined,
      rhythmType: typeof raw.rhythmType === 'string' ? raw.rhythmType : 'daily',
      rhythmConfig: raw.rhythmConfig && typeof raw.rhythmConfig === 'object' ? raw.rhythmConfig : undefined,
      reminderTime: typeof raw.reminderTime === 'string' ? raw.reminderTime : undefined,
      createdAt: typeof raw.createdAt === 'string' && raw.createdAt.includes('-') ? raw.createdAt : todayLocalDate(),
      trackingStyle: raw.trackingStyle || (raw.trackStreaks ? 'structured' : 'gentle'),
      trackStreaks: Boolean(raw.trackStreaks ?? (raw.trackingStyle === 'structured')),
      notes: typeof raw.notes === 'string' ? raw.notes : undefined,
    };
  };

  const safeCurrent = Array.isArray(current) ? current.map(sanitize).filter((h): h is HabitItem => h !== null) : [];
  const safeReclaimed = Array.isArray(reclaimed) ? reclaimed.map(sanitize).filter((h): h is HabitItem => h !== null) : [];

  const map = new Map<string, HabitItem>();

  // Add reclaimed first, then overlay current
  for (const h of safeReclaimed) {
    map.set(h.id, h);
  }

  for (const h of safeCurrent) {
    const existing = map.get(h.id);
    if (existing) {
      map.set(h.id, {
        ...existing,
        ...h,
        createdAt: existing.createdAt || h.createdAt,
        rhythmConfig: h.rhythmConfig || existing.rhythmConfig,
        reminderTime: h.reminderTime ?? existing.reminderTime,
        notes: h.notes ?? existing.notes,
      });
    } else {
      map.set(h.id, h);
    }
  }

  return Array.from(map.values());
}

/**
 * Cojoin two habit event arrays by (habitId, date).
 */
export function cojoinHabitEvents(current: any[] = [], reclaimed: any[] = []): HabitEventItem[] {
  const sanitize = (raw: any): HabitEventItem | null => {
    if (!raw || typeof raw !== 'object') return null;
    if (typeof raw.habitId !== 'string' || typeof raw.date !== 'string') return null;
    if (!raw.habitId.trim() || !raw.date.trim()) return null;
    return { habitId: raw.habitId.trim(), date: raw.date.trim() };
  };

  const safeCurrent = Array.isArray(current) ? current.map(sanitize).filter((e): e is HabitEventItem => e !== null) : [];
  const safeReclaimed = Array.isArray(reclaimed) ? reclaimed.map(sanitize).filter((e): e is HabitEventItem => e !== null) : [];

  const set = new Set<string>();
  const merged: HabitEventItem[] = [];

  for (const e of [...safeCurrent, ...safeReclaimed]) {
    const key = `${e.habitId}:${e.date}`;
    if (!set.has(key)) {
      set.add(key);
      merged.push(e);
    }
  }

  return merged;
}

/**
 * Cojoin general arrays of objects with an `id` field.
 */
export function cojoinIdentifiedItems<T extends { id: string }>(current: T[] = [], reclaimed: T[] = []): T[] {
  const safeCurrent = Array.isArray(current) ? current.filter(i => i && typeof i === 'object' && typeof (i as any).id === 'string') : [];
  const safeReclaimed = Array.isArray(reclaimed) ? reclaimed.filter(i => i && typeof i === 'object' && typeof (i as any).id === 'string') : [];

  const map = new Map<string, T>();
  for (const item of safeReclaimed) {
    map.set(item.id, item);
  }
  for (const item of safeCurrent) {
    const existing = map.get(item.id);
    if (existing) {
      map.set(item.id, { ...existing, ...item });
    } else {
      map.set(item.id, item);
    }
  }
  return Array.from(map.values());
}

/**
 * Cojoin raw key-value dictionaries for an app namespace.
 */
export function cojoinAppData(
  appId: string,
  targetData: Record<string, any>,
  sourceData: Record<string, any>
): Record<string, any> {
  const result: Record<string, any> = { ...targetData };

  for (const [key, sourceVal] of Object.entries(sourceData)) {
    if (sourceVal === undefined || sourceVal === null) continue;
    const targetVal = result[key];

    if (targetVal === undefined || targetVal === null) {
      result[key] = sourceVal;
      continue;
    }

    // App-specific key cojoining rules
    if (appId.includes('habits')) {
      if (key === 'habits') {
        const cHabits = Array.isArray(targetVal) ? targetVal : (targetVal?.habits ?? []);
        const sHabits = Array.isArray(sourceVal) ? sourceVal : (sourceVal?.habits ?? []);
        result[key] = cojoinHabits(cHabits, sHabits);
        continue;
      }
      if (key === 'events') {
        const cEvts = Array.isArray(targetVal) ? targetVal : (targetVal?.events ?? []);
        const sEvts = Array.isArray(sourceVal) ? sourceVal : (sourceVal?.events ?? []);
        result[key] = cojoinHabitEvents(cEvts, sEvts);
        continue;
      }
      if (key === 'achievements') {
        const cAch = Array.isArray(targetVal) ? targetVal : [];
        const sAch = Array.isArray(sourceVal) ? sourceVal : [];
        const achMap = new Map<string, any>();
        for (const a of [...sAch, ...cAch]) {
          if (a && typeof a === 'object' && a.achievementId) {
            achMap.set(`${a.achievementId}:${a.habitId || ''}`, a);
          }
        }
        result[key] = Array.from(achMap.values());
        continue;
      }
    }

    if (appId.includes('docs')) {
      if (key === 'nodeIds') {
        const cIds = Array.isArray(targetVal) ? targetVal : [];
        const sIds = Array.isArray(sourceVal) ? sourceVal : [];
        result[key] = Array.from(new Set([...cIds, ...sIds]));
        continue;
      }
      if (key.startsWith('node:')) {
        result[key] = { ...sourceVal, ...targetVal };
        continue;
      }
      if (key.startsWith('doc:')) {
        // If one is empty, take the non-empty one; else keep target (most recent)
        result[key] = (typeof targetVal === 'string' && targetVal.length > 0) ? targetVal : sourceVal;
        continue;
      }
    }

    if (appId.includes('todo')) {
      if (key === 'listIds') {
        const cIds = Array.isArray(targetVal?.ids) ? targetVal.ids : [];
        const sIds = Array.isArray(sourceVal?.ids) ? sourceVal.ids : [];
        result[key] = {
          ids: Array.from(new Set([...cIds, ...sIds])),
          selectedListId: targetVal?.selectedListId ?? sourceVal?.selectedListId ?? null,
        };
        continue;
      }
      if (key.startsWith('list:')) {
        const cItems = Array.isArray(targetVal?.items) ? targetVal.items : [];
        const sItems = Array.isArray(sourceVal?.items) ? sourceVal.items : [];
        result[key] = {
          ...sourceVal,
          ...targetVal,
          items: cojoinIdentifiedItems(cItems, sItems),
        };
        continue;
      }
    }

    if (appId.includes('calendar')) {
      if (key === 'events' || key === 'routineBlocks' || key === 'eventGroups') {
        result[key] = cojoinIdentifiedItems(targetVal, sourceVal);
        continue;
      }
    }

    if (appId.includes('shopping-list')) {
      if (key === 'lists') {
        const cLists = Array.isArray(targetVal) ? targetVal : [];
        const sLists = Array.isArray(sourceVal) ? sourceVal : [];
        const mergedLists = cojoinIdentifiedItems(cLists, sLists).map(list => {
          const sList = sLists.find(l => l.id === list.id);
          const cList = cLists.find(l => l.id === list.id);
          const items = cojoinIdentifiedItems(cList?.items ?? [], sList?.items ?? []);
          return { ...list, items };
        });
        result[key] = mergedLists;
        continue;
      }
    }

    if (appId.includes('training')) {
      if (key === 'exercises' || key === 'programs' || key === 'logged_workouts' || key === 'body_metrics') {
        result[key] = cojoinIdentifiedItems(targetVal, sourceVal);
        continue;
      }
    }

    if (appId.includes('finance')) {
      if (key === 'invoices' || key === 'templates') {
        result[key] = cojoinIdentifiedItems(targetVal, sourceVal);
        continue;
      }
    }

    if (appId.includes('contacts')) {
      if (key === 'contacts') {
        result[key] = cojoinIdentifiedItems(targetVal, sourceVal);
        continue;
      }
    }

    if (appId.includes('meal-planner')) {
      if (key === 'planned_meals' || key === 'recipes' || key === 'meal_backlog' || key === 'cravings') {
        result[key] = cojoinIdentifiedItems(targetVal, sourceVal);
        continue;
      }
    }

    // Generic key handling
    if (key.startsWith('tool:configured:')) {
      result[key] = Boolean(targetVal || sourceVal);
      continue;
    }

    if (Array.isArray(targetVal) && Array.isArray(sourceVal)) {
      if (targetVal.length > 0 && typeof targetVal[0] === 'object' && targetVal[0]?.id) {
        result[key] = cojoinIdentifiedItems(targetVal, sourceVal);
      } else {
        result[key] = Array.from(new Set([...targetVal, ...sourceVal]));
      }
      continue;
    }

    if (typeof targetVal === 'object' && typeof sourceVal === 'object' && targetVal !== null && sourceVal !== null) {
      result[key] = { ...sourceVal, ...targetVal };
      continue;
    }

    // Default: target wins over source for non-empty primitives
    result[key] = targetVal ?? sourceVal;
  }

  return result;
}

/**
 * Cojoin source Y.Doc map into target Y.Doc map
 */
export function cojoinYDocs(appId: string, targetDoc: Y.Doc, sourceDoc: Y.Doc): boolean {
  const targetMap = targetDoc.getMap('storage');
  const sourceMap = sourceDoc.getMap('storage');

  const targetData: Record<string, any> = {};
  targetMap.forEach((v, k) => { targetData[k] = v; });

  const sourceData: Record<string, any> = {};
  sourceMap.forEach((v, k) => { sourceData[k] = v; });

  if (Object.keys(sourceData).length === 0) return false;

  const cojoined = cojoinAppData(appId, targetData, sourceData);

  let hasChanges = false;
  targetDoc.transact(() => {
    for (const [k, v] of Object.entries(cojoined)) {
      const existing = targetMap.get(k);
      if (JSON.stringify(existing) !== JSON.stringify(v)) {
        targetMap.set(k, v);
        hasChanges = true;
      }
    }
  });

  return hasChanges;
}

const KNOWN_APP_PREFIXES = [
  '@yada/app-shopping-list',
  '@yada/app-meal-planner',
  '@yada/app-content-distribution',
  '@yada/app-habits',
  '@yada/app-docs',
  '@yada/app-todo',
  '@yada/app-calendar',
  '@yada/app-finance',
  '@yada/app-training',
  '@yada/app-contacts',
  '@yada/app-chat',
  '@yada/app-groups',
  '@yada/app-emoji',
  'chat-messages',
];

/**
 * Extract app ID / namespace from a document ID.
 * Examples:
 *   "@yada/app-habits-123e4567" -> "@yada/app-habits"
 *   "@yada/app-docs-abc-123" -> "@yada/app-docs"
 *   "chat-messages-123e4567" -> "chat-messages"
 *   "@yada/app-docs" -> "@yada/app-docs"
 */
export function extractAppIdFromDocumentId(docId: string): string {
  for (const prefix of KNOWN_APP_PREFIXES) {
    if (docId === prefix || docId.startsWith(`${prefix}-`)) {
      return prefix;
    }
  }

  const lastDash = docId.lastIndexOf('-');
  if (lastDash > 0) {
    return docId.slice(0, lastDash);
  }
  return docId;
}

/**
 * Read browser localStorage keys for an app and cojoin them into a Y.Doc
 */
export function cojoinLocalStorageIntoYDoc(appId: string, doc: Y.Doc): boolean {
  if (typeof localStorage === 'undefined') return false;

  // Never cojoin public/social unencrypted apps into private workspace documents
  if (appId.startsWith('@yada/app-community-votes') || appId.startsWith('@yada/app-public-')) {
    return false;
  }

  const prefix = `yada:${appId}:`;
  const localData: Record<string, any> = {};

  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(prefix)) {
      const storageKey = k.slice(prefix.length);
      const raw = localStorage.getItem(k);
      if (raw !== null && raw !== 'null' && raw !== 'undefined') {
        try {
          localData[storageKey] = JSON.parse(raw);
        } catch {
          localData[storageKey] = raw;
        }
      }
    }
  }

  if (Object.keys(localData).length === 0) return false;

  const targetMap = doc.getMap('storage');
  const targetData: Record<string, any> = {};
  targetMap.forEach((v, k) => { targetData[k] = v; });

  const cojoined = cojoinAppData(appId, targetData, localData);

  let hasChanges = false;
  doc.transact(() => {
    for (const [k, v] of Object.entries(cojoined)) {
      const existing = targetMap.get(k);
      if (JSON.stringify(existing) !== JSON.stringify(v)) {
        targetMap.set(k, v);
        hasChanges = true;
      }
    }
  });

  return hasChanges;
}

/**
 * Sanitize and defensively clean up a storage map in-place.
 *
 * Runs on every open, including on a device whose copy has not received the
 * remote state yet. Each key below holds a whole value, so a write here races
 * the remote one and either side can win the merge: a key is therefore never
 * created from nothing, and an index is rewritten only when entries are missing.
 */
export function sanitizeStorageMap(appId: string, map: Y.Map<any>): void {
  if (appId.includes('habits')) {
    const rawHabits = map.get('habits');
    const rawEvents = map.get('events');

    let habitsArray: any[] = [];
    if (Array.isArray(rawHabits)) {
      habitsArray = rawHabits;
    } else if (rawHabits && typeof rawHabits === 'object' && Array.isArray(rawHabits.habits)) {
      habitsArray = rawHabits.habits;
    }

    let eventsArray: any[] = [];
    if (Array.isArray(rawEvents)) {
      eventsArray = rawEvents;
    } else if (rawEvents && typeof rawEvents === 'object' && Array.isArray(rawEvents.events)) {
      eventsArray = rawEvents.events;
    }

    const cleanHabits = cojoinHabits(habitsArray, []);
    const cleanEvents = cojoinHabitEvents(eventsArray, []);

    if (rawHabits !== undefined && JSON.stringify(rawHabits) !== JSON.stringify(cleanHabits)) {
      map.set('habits', cleanHabits);
    }
    if (rawEvents !== undefined && JSON.stringify(rawEvents) !== JSON.stringify(cleanEvents)) {
      map.set('events', cleanEvents);
    }
    if (cleanHabits.length > 0 && !map.get('tool:configured:@yada/app-habits')) {
      map.set('tool:configured:@yada/app-habits', true);
    }
  }

  if (appId.includes('docs')) {
    const rawNodeIds = map.get('nodeIds');
    const nodeIdsArray = Array.isArray(rawNodeIds) ? rawNodeIds : [];
    const discoveredNodeIds: string[] = [];

    map.forEach((_val, k) => {
      if (typeof k === 'string' && k.startsWith('node:')) {
        discoveredNodeIds.push(k.slice('node:'.length));
      }
    });

    const allNodeIds = Array.from(new Set([...nodeIdsArray, ...discoveredNodeIds]));
    if (allNodeIds.length > nodeIdsArray.length) {
      map.set('nodeIds', allNodeIds);
    }
  }

  if (appId.includes('todo')) {
    const rawListIndex = map.get('listIds');
    const listIds = Array.isArray(rawListIndex?.ids) ? rawListIndex.ids : [];
    const discoveredListIds: string[] = [];

    map.forEach((_val, k) => {
      if (typeof k === 'string' && k.startsWith('list:')) {
        discoveredListIds.push(k.slice('list:'.length));
      }
    });

    const allListIds = Array.from(new Set([...listIds, ...discoveredListIds]));
    if (allListIds.length > 0 && JSON.stringify(listIds) !== JSON.stringify(allListIds)) {
      map.set('listIds', {
        ids: allListIds,
        selectedListId: rawListIndex?.selectedListId ?? allListIds[0] ?? null,
      });
    }
  }
}

export interface ProfileReconciliationResult {
  /** Map from old local profileId to target server profileId */
  idMapping: Record<string, string>;
  /** Profiles from local state that should be re-created on the server */
  profilesToCreate: any[];
  /** Profiles that were cleanly matched and mapped */
  autoMapped: Array<{ localId: string; serverId: string; localName: string; serverName: string }>;
  /** Profiles that require user confirmation / interactive choice */
  ambiguous: Array<{ localProfile: any; candidateServerProfiles: any[] }>;
}

/**
 * Plan profile reconciliation between local storage and server state.
 * Detects 1:1 obvious matches and flags ambiguous cases.
 */
export function planProfileReconciliation(
  localProfiles: any[] = [],
  serverProfiles: any[] = []
): ProfileReconciliationResult {
  const idMapping: Record<string, string> = {};
  const profilesToCreate: any[] = [];
  const autoMapped: Array<{ localId: string; serverId: string; localName: string; serverName: string }> = [];
  const ambiguous: Array<{ localProfile: any; candidateServerProfiles: any[] }> = [];

  const serverById = new Map<string, any>(serverProfiles.map(p => [p.id, p]));
  const matchedServerIds = new Set<string>();

  // 1. Exact ID matches
  for (const local of localProfiles) {
    if (serverById.has(local.id)) {
      idMapping[local.id] = local.id;
      matchedServerIds.add(local.id);
    }
  }

  // 2. Unmatched local profiles
  const unmatchedLocals = localProfiles.filter(l => !idMapping[l.id]);
  const unmatchedServers = serverProfiles.filter(s => !matchedServerIds.has(s.id));

  // If server is completely empty (DB was dropped and recreated without default profiles yet),
  // all local profiles should simply be re-created on the server.
  if (serverProfiles.length === 0) {
    return {
      idMapping,
      profilesToCreate: unmatchedLocals,
      autoMapped: [],
      ambiguous: [],
    };
  }

  // If 1:1 exact profile types and names or single standard profile on both sides
  for (const local of unmatchedLocals) {
    // Look for exact profile_type + exact name match
    const exactNameMatch = unmatchedServers.find(
      s => !matchedServerIds.has(s.id) &&
           s.profile_type === (local.profile_type || local.profileType) &&
           s.name?.trim().toLowerCase() === local.name?.trim().toLowerCase()
    );

    if (exactNameMatch) {
      idMapping[local.id] = exactNameMatch.id;
      matchedServerIds.add(exactNameMatch.id);
      autoMapped.push({
        localId: local.id,
        serverId: exactNameMatch.id,
        localName: local.name,
        serverName: exactNameMatch.name,
      });
      continue;
    }

    // Check if both sides have only 1 profile of this type (e.g. single standard profile)
    const localSameType = localProfiles.filter(l => (l.profile_type || l.profileType) === (local.profile_type || local.profileType));
    const serverSameType = serverProfiles.filter(s => (s.profile_type || s.profileType) === (local.profile_type || local.profileType));

    if (localSameType.length === 1 && serverSameType.length === 1 && !matchedServerIds.has(serverSameType[0].id)) {
      const targetServer = serverSameType[0];
      idMapping[local.id] = targetServer.id;
      matchedServerIds.add(targetServer.id);
      autoMapped.push({
        localId: local.id,
        serverId: targetServer.id,
        localName: local.name,
        serverName: targetServer.name,
      });
      continue;
    }

    // If ambiguous (multiple candidates or no obvious match), register as ambiguous
    const candidates = serverProfiles.filter(s => (s.profile_type || s.profileType) === (local.profile_type || local.profileType));
    ambiguous.push({
      localProfile: local,
      candidateServerProfiles: candidates.length > 0 ? candidates : serverProfiles,
    });
  }

  return {
    idMapping,
    profilesToCreate,
    autoMapped,
    ambiguous,
  };
}

/**
 * Normalizes profile ordering to enforce the invariant:
 * A household profile must NEVER land before the main profile (standard / internal / primary persona).
 */
export function normalizeProfileSyncOrder<T extends { profile_type?: string; profileType?: string; id?: string }>(
  profiles: T[]
): T[] {
  if (!profiles || profiles.length <= 1) return profiles ? [...profiles] : [];

  const getType = (p: T): string => (p.profile_type || p.profileType || 'standard').toLowerCase();

  const householdIndex = profiles.findIndex(p => getType(p) === 'household');
  if (householdIndex === -1) {
    return [...profiles];
  }

  // Find main profile index: prefer 'standard', then 'internal', then first non-household
  let mainIndex = profiles.findIndex(p => getType(p) === 'standard');
  if (mainIndex === -1) {
    mainIndex = profiles.findIndex(p => getType(p) === 'internal');
  }
  if (mainIndex === -1) {
    mainIndex = profiles.findIndex(p => getType(p) !== 'household');
  }

  // If there is no non-household profile, nothing to reorder
  if (mainIndex === -1) {
    return [...profiles];
  }

  // If household lands before main profile, move main profile before the first household profile
  if (householdIndex < mainIndex) {
    const result = [...profiles];
    const [mainProfile] = result.splice(mainIndex, 1);
    result.splice(householdIndex, 0, mainProfile);
    return result;
  }

  return [...profiles];
}

/**
 * Orders profiles according to a specified list of profile IDs, while ensuring the
 * invariant that household profiles never land before the main profile.
 */
export function applyProfileOrder<T extends { id?: string; profile_type?: string; profileType?: string }>(
  profiles: T[],
  orderIds?: string[]
): T[] {
  if (!orderIds || orderIds.length === 0) {
    return normalizeProfileSyncOrder(profiles);
  }

  const idMap = new Map<string, T>(profiles.map(p => [p.id || '', p]));
  const ordered: T[] = [];

  for (const id of orderIds) {
    const p = idMap.get(id);
    if (p) {
      ordered.push(p);
      idMap.delete(id);
    }
  }

  for (const p of idMap.values()) {
    ordered.push(p);
  }

  return normalizeProfileSyncOrder(ordered);
}
